import { FormModal } from '~/shared/forms/FormModal';
import type { FormValues } from '~/shared/forms/types';
import { editPredefinedGoalOptions, getFullGoalDefinitionOptions, type EditPredefinedGoalCommand, type FullOrderViewModel } from '@generated';
import { convertClientValuesToServerValues, getInitialValues } from '~/libraries/forms/values';
import Yup from '~/util/yup';
import { generateValidationSchema } from '~/libraries/forms/validation';
import { FormFields } from '~/libraries/forms/FormFields';
import { useSuspenseQuery } from '@tanstack/react-query';
import { convertClientDateToServerDate } from '~/util/date';
import { useGoalsAndnterventionsForOrder } from '../queries';

export function EditPredefinedGoalFormModal({
	goalId,
	fullOrder,
	closeModal
}: {
	goalId: string;
	fullOrder: FullOrderViewModel;
	closeModal: () => void;
}) {
	const goal = fullOrder.goals.find(goal => goal.id === goalId);
	if (!goal) {
		throw new Error('Goal not found');
	} else if (!goal.goalDefinition) {
		throw new Error('Goal definition not found');
	} else if (!goal.goalDefinition.hasDefinitionFields) {
		throw new Error('Goal definition form fields not found');
	}
	const { refetch } = useSuspenseQuery(useGoalsAndnterventionsForOrder(fullOrder.id));
	const { data: fullGoalDefinition } = useSuspenseQuery(
		getFullGoalDefinitionOptions(goal.goalDefinition.id, convertClientDateToServerDate(fullOrder.certificationPeriod.startDate))
	);

	const initialValues = getInitialValues(fullGoalDefinition.definitionFormFields, goal.definitionValues);

	const validationSchema = Yup.object().shape({ ...generateValidationSchema(fullGoalDefinition.definitionFormFields) });

	const onSaveTransform = (values: FormValues): EditPredefinedGoalCommand => {
		return {
			orderId: fullOrder.id,
			goalId: goal.id,
			goalDefinitionValues: values ? convertClientValuesToServerValues(values, fullGoalDefinition.definitionFormFields) : undefined
		};
	};

	return (
		<FormModal
			title="Edit Goal"
			closeModal={closeModal}
			initialValues={{
				...initialValues
			}}
			saveMutation={editPredefinedGoalOptions}
			onSuccess={() => {
				refetch();
				closeModal();
			}}
			onSaveTransform={onSaveTransform}
			validationSchema={validationSchema}
		>
			<div className="border-b border-gray-100 px-5 py-4 ">
				<p className="text-base font-bold text-gray-900">{goal.goalDefinition.title}</p>
				<p className="mt-0.5 text-sm text-gray-500">{goal.goalDefinition.definitionText}</p>
			</div>
			<FormFields formControls={fullGoalDefinition.definitionFormControls} formFields={fullGoalDefinition.definitionFormFields} />
		</FormModal>
	);
}
